import React from "react";

type ButtonProps = React.PropsWithChildren & {
  ref?: React.RefObject<HTMLButtonElement | null>;
  className?: string;
  color?: ButtonColor;
  hoverText?: string;
  title?: string;
  disabled?: boolean;
  size?: "small" | "medium" | "large" | "mediumDynamic";
} & (
    | {
        type: "submit";
        onClick?: (e: React.FormEvent) => void;
      }
    | {
        type?: "button" | "reset";
        onClick: (e: React.MouseEvent<HTMLElement>) => void;
      }
  ) &
  Pick<
    React.HTMLAttributes<HTMLButtonElement>,
    "onMouseEnter" | "onMouseLeave"
  >;

export enum ButtonColor {
  Stone = "bg-gray-4 text-white hover:bg-[#444]",
  Green = "bg-green text-white hover:bg-[#4d8c1d] border border-green",
  GreenOutLine = "border border-green text-green hover:bg-green/10",
  Red = "bg-red-100 !text-red-500 hover:bg-red-200",
  RedOutline = "border border-red-500 text-red-500",
  Light = "bg-zinc-200/60 border border-[#efeff1]",
  LightHover = "bg-zinc-200/60 hover:bg-zinc-200/80 !text-zinc-500",
  Blue = "bg-[#318dde] text-white",
  BlueOutline = "border border-[#318dde] text-[#318dde] hover:bg-[#318dde]/10",
  Yellow = "bg-yellow-600",
  Transparent = "bg-transparent hover:bg-black/5 text-black !px-2",
  Grey = "bg-zinc-200 !text-black hover:bg-zinc-300",
  Outline = "border border-gray-2 text-black",
  White = "border border-gray-2 text-black bg-white hover:bg-zinc-50",
  Black = "bg-zinc-800 hover:bg-zinc-900 text-white border border-zinc-800 active:bg-zinc-700",
}

const Button: React.FC<ButtonProps> = ({
  ref,
  title,
  onClick,
  children,
  className,
  color: colorProp,
  type = "button",
  disabled = false,
  onMouseEnter,
  hoverText,
  onMouseLeave,
  size = "medium",
}) => {
  const color = colorProp ?? ButtonColor.White;

  const sizeClass = {
    small: "px-3 py-1.5 text-sm",
    medium: "px-4 py-2 text-base",
    mediumDynamic: "px-2 md:px-4 py-1 md:py-1.5 text-base",
    large: "px-6 py-3 text-lg",
  }[size];

  return (
    <button
      ref={ref}
      type={type}
      title={title}
      className={`${sizeClass} font-medium rounded w-fit h-fit flex items-center justify-center border-box relative group ${
        disabled ? "opacity-50 !cursor-not-allowed" : ``
      } ${color} ${
        color === ButtonColor.Light ? "!text-zinc-800" : ""
      } ${className} `}
      style={{
        fontWeight: 450,
      }}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
      {hoverText && (
        <div className="absolute -top-[110%] left-1/2 transform -translate-x-1/2 whitespace-nowrap bg-black/50 text-white text-sm p-2 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-150">
          {hoverText}
        </div>
      )}
    </button>
  );
};

export default Button;
